import React from "react";
import { motion, Variants } from "framer-motion";

/** Shape HugeIcons ships each icon as: an array of [tag, svgAttrs] tuples. */
export type HugeIconData = Array<[string, Record<string, any>]>;

export type IconAnimationPreset = {
    /** Per-path-index variants, keyed to that path's position in the icon's data array. */
    parts?: Record<number, Variants>;
    /** Applied to any path index not covered by `parts`. Defaults to a no-op. */
    defaultPart?: Variants;
};

const NOOP_PART: Variants = { rest: {}, hover: {} };

/**
 * Renders a HugeIcons icon as individually-animatable strokes instead of one
 * flat glyph, so hover can move a specific part (a door swinging, an arrow
 * sliding out) rather than scaling the icon as a whole. Falls back to NOOP_PART
 * for any path a preset doesn't name, so icons without a bespoke preset just
 * render statically instead of guessing at motion that fits their shape.
 */
export const AnimatedIcon: React.FC<{
    icon: HugeIconData;
    size?: number;
    hovered: boolean;
    preset?: IconAnimationPreset;
}> = ({ icon, size = 20, hovered, preset }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: "visible", display: "block" }}
    >
        {icon.map(([, attrs], index) => {
            const { key, ...rest } = attrs;
            const variants = preset?.parts?.[index] ?? preset?.defaultPart ?? NOOP_PART;
            return (
                <motion.path
                    key={key ?? index}
                    {...rest}
                    variants={variants}
                    initial="rest"
                    animate={hovered ? "hover" : "rest"}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                />
            );
        })}
    </svg>
);
